/**
 * ViewportContext.tsx
 *
 * React context that makes the current canvas Viewport available to any
 * descendant — including components inside react-konva's Stage/Layer tree,
 * which live in a separate React renderer and cannot access context normally.
 *
 * The Viewport describes the rectangular area on the canvas where the pitch
 * is drawn:  { x, y, w, h }  — all in canvas pixel coordinates.
 *
 * ─── Standard React tree ────────────────────────────────────────────────────
 *
 *   <ViewportProvider viewport={vp}>
 *     <SomeHtmlOverlay />     ← useViewport() works normally
 *
 *     <Stage>
 *       <Layer>
 *         {/* ❌ useViewport() would return null here — wrong renderer *\/}
 *       </Layer>
 *     </Stage>
 *   </ViewportProvider>
 *
 * ─── With react-konva bridge ─────────────────────────────────────────────────
 *
 *   <ViewportProvider viewport={vp}>
 *     <SomeHtmlOverlay />     ← useViewport() ✓
 *
 *     <Stage>
 *       <ViewportBridge>      ← re-injects the context into Konva's renderer
 *         <Layer>
 *           <KonvaPlayerDot /> ← useViewport() ✓
 *         </Layer>
 *       </ViewportBridge>
 *     </Stage>
 *   </ViewportProvider>
 *
 * ─── Why this happens ────────────────────────────────────────────────────────
 *
 * react-konva uses a custom React reconciler (react-konva uses
 * `react-reconciler` internally). Each reconciler maintains its own context
 * store. A Provider rendered by the DOM reconciler is invisible to components
 * rendered by the Konva reconciler — they see the default context value (null).
 *
 * The fix is to read the value in the DOM tree (where it IS available) and
 * re-provide it inside the Konva tree via a second Provider rendered by the
 * Konva reconciler. `<ViewportBridge>` does exactly this — it must be placed
 * as a *direct child* of a Konva container (Stage, Layer, Group, etc.).
 */

import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type Dispatch,
  type ReactNode,
  type SetStateAction,
} from "react";

import type { Viewport } from "./types";

// ─── CONTEXT ─────────────────────────────────────────────────────────────────

/**
 * The single context object shared by both the DOM and Konva renderers.
 * Exported so ViewportBridge can reference the same instance.
 */
export const ViewportContext = createContext<Viewport | null>(null);
export const ViewportSetterContext = createContext<Dispatch<SetStateAction<Viewport>> | null>(null);

ViewportContext.displayName = "ViewportContext";
ViewportSetterContext.displayName = "ViewportSetterContext";

// ─── PROVIDER (DOM tree) ─────────────────────────────────────────────────────

interface ViewportProviderProps {
  viewport: Viewport;
  children: ReactNode;
}

/**
 * Place this in the normal React/DOM tree, wrapping both HTML children and the
 * Konva `<Stage>`. Children in the DOM tree can call `useViewport()` directly.
 * Children inside the Konva tree must be wrapped in `<ViewportBridge>`.
 *
 * The value is memoised on the four numeric fields so that referential identity
 * is stable between renders unless the dimensions actually change.
 *
 * Children can update the viewport via `useSetViewport()`. External prop
 * changes take precedence and will overwrite any local updates.
 */
export function ViewportProvider({ viewport, children }: ViewportProviderProps) {
  const [vp, setVp] = useState<Viewport>(viewport);

  // Sync when the parent passes a new viewport (e.g. on resize).
  useEffect(() => {
    setVp({ x: viewport.x, y: viewport.y, w: viewport.w, h: viewport.h });
  }, [viewport.x, viewport.y, viewport.w, viewport.h]);

  const stable = useMemo(
    () => ({ x: vp.x, y: vp.y, w: vp.w, h: vp.h }),
    [vp.x, vp.y, vp.w, vp.h],
  );

  return (
    <ViewportSetterContext.Provider value={setVp}>
      <ViewportContext.Provider value={stable}>
        {children}
      </ViewportContext.Provider>
    </ViewportSetterContext.Provider>
  );
}

// ─── BRIDGE (Konva tree) ──────────────────────────────────────────────────────

/**
 * Re-injects the ViewportContext value into react-konva's separate renderer.
 *
 * Must be placed *inside* a Konva container (Stage, Layer, or Group) and
 * *outside* the `<ViewportProvider>` in the Konva sense — it reads from the
 * DOM context and re-provides it for the Konva subtree.
 *
 * Implementation note: react-konva v18+ exposes a `KonvaReactBridge` utility,
 * but it requires opt-in configuration. The pattern below works across all
 * versions by simply reading the context value in the nearest DOM ancestor
 * (which is still accessible via `useContext` when this component itself is
 * rendered by the DOM reconciler as the bridge entry point) and re-providing
 * it into the Konva renderer through a second `<ViewportContext.Provider>`.
 *
 * Usage:
 *   <Stage>
 *     <ViewportBridge>
 *       <Layer>
 *         <MyKonvaShape />   ← useViewport() works here
 *       </Layer>
 *     </ViewportBridge>
 *   </Stage>
 */
export function ViewportBridge({ children }: { children: ReactNode }) {
  // This component is rendered by the DOM reconciler (it is a direct child of
  // Stage which delegates rendering of *its own* children to the DOM reconciler
  // before handing off to Konva). At this boundary we can still read the DOM
  // context value.
  const value = useContext(ViewportContext);
  const setter = useContext(ViewportSetterContext);

  // Re-provide both — these Providers are rendered by the Konva reconciler,
  // making the value and setter visible to all Konva descendants.
  return (
    <ViewportSetterContext.Provider value={setter}>
      <ViewportContext.Provider value={value}>
        {children}
      </ViewportContext.Provider>
    </ViewportSetterContext.Provider>
  );
}

// ─── HOOKS ───────────────────────────────────────────────────────────────────

/**
 * Returns the current viewport. Works in both the DOM tree and inside a Konva
 * tree wrapped with `<ViewportBridge>`.
 *
 * Throws if called outside any `<ViewportProvider>` / `<ViewportBridge>`.
 */
export function useViewport(): Viewport {
  const ctx = useContext(ViewportContext);
  if (!ctx) {
    throw new Error(
      "useViewport() must be used inside a <ViewportProvider>.\n" +
      "If you are inside a react-konva Stage/Layer, also wrap with <ViewportBridge>.",
    );
  }
  return ctx;
}

/**
 * Like `useViewport()` but returns `null` instead of throwing.
 * Useful for optional overlay components that may render outside a provider.
 */
export function useViewportSafe(): Viewport | null {
  return useContext(ViewportContext);
}

/**
 * Returns the setter for the viewport managed by the nearest
 * `<ViewportProvider>`. Works in both the DOM tree and inside a Konva tree
 * wrapped with `<ViewportBridge>`.
 *
 * Throws if called outside any `<ViewportProvider>` / `<ViewportBridge>`.
 */
export function useSetViewport(): Dispatch<SetStateAction<Viewport>> {
  const setter = useContext(ViewportSetterContext);
  if (!setter) {
    throw new Error(
      "useSetViewport() must be used inside a <ViewportProvider>.\n" +
      "If you are inside a react-konva Stage/Layer, also wrap with <ViewportBridge>.",
    );
  }
  return setter;
}

// ─── UTILITIES ────────────────────────────────────────────────────────────────

/**
 * Convenience hook that derives common coordinate conversions from the viewport.
 *
 * Works in both DOM and Konva trees (with `<ViewportBridge>`).
 */
export function useViewportUtils() {
  const vp = useViewport();

  return useMemo(() => ({
    /**
     * Canonical { u, v } ∈ [0,1]² → canvas pixel { x, y }.
     * Use for positioning Konva shapes (x/y props) or absolutely-placed HTML.
     *
     * v is flipped because canvas y grows downward.
     */
    toCanvas(uv: { u: number; v: number }): { x: number; y: number } {
      return {
        x: vp.x + uv.u * vp.w,
        y: vp.y + (1 - uv.v) * vp.h,
      };
    },

    /**
     * Canvas pixel { x, y } → canonical { u, v }.
     * Use for hit-testing pointer events on the Konva stage.
     */
    fromCanvas(x: number, y: number): { u: number; v: number } {
      return {
        u: (x - vp.x) / vp.w,
        v: 1 - (y - vp.y) / vp.h,
      };
    },

    /** The raw viewport, for cases that need { x, y, w, h } directly. */
    vp,
  }), [vp]);
}

// ─── RE-EXPORT TYPE ───────────────────────────────────────────────────────────
export type { Viewport };
